/*
 * Field Mode's standing switch: Collection mode (a specimen is taken, and the
 * record gets an Isolate ID and plates) or Observation mode (nothing is
 * taken: a sighting with no ID and no plates). docs/FIELD_AI_VISION_SPEC.md,
 * Part I.
 *
 * The switch decides one thing: what a NEW batch starts as. Each batch keeps
 * its own `details.observeOnly`, which the outbox and the server already
 * act on (fieldOutbox.js's mint step; collections.py submit_collection makes
 * plates only for a record with a code). Changing the mode never rewrites a
 * batch, and the gallery's "Sighting only" box still overrides it per batch.
 *
 * Per person and per device, kept as {[userId]: 'collection' | 'observation'}
 * under one key, as utils/fieldAutoAi.js keeps its choice: a phone shared by
 * two collectors keeps each one's. Someone who may not mint a specimen
 * (no specimen:register) is always in Observation mode, whatever is stored.
 *
 * A `storage` event never reaches the tab that wrote it, so a change is also
 * announced on `window` (CHANGE_EVENT).
 */
import { useEffect, useState } from 'react';
import { normaliseTag } from './specimenCode';

export const FIELD_MODE_KEY = 'mb_field_mode';
export const CHANGE_EVENT = 'pitriot:field-mode';
export const COLLECTION = 'collection';
export const OBSERVATION = 'observation';
export const MODES = Object.freeze([COLLECTION, OBSERVATION]);

// What this page was told when the storage would not keep it.
const unkept = new Map();

function storageOf(storage) {
  if (storage) return storage;
  try { return globalThis.localStorage; } catch { return null; }
}

function readMap(store) {
  try {
    const raw = store?.getItem(FIELD_MODE_KEY);
    const map = raw ? JSON.parse(raw) : {};
    return map && typeof map === 'object' && !Array.isArray(map) ? map : {};
  } catch {
    return {};
  }
}

/** This person's mode on this device. Collection by default for whoever may
 *  collect; Observation, always, for whoever may not. */
export function readFieldMode(userId, canCollect, storage) {
  if (!canCollect) return OBSERVATION;
  const key = String(userId ?? '');
  const held = unkept.has(key) ? unkept.get(key) : readMap(storageOf(storage))[key];
  return held === OBSERVATION ? OBSERVATION : COLLECTION;
}

export function setFieldMode(userId, mode, storage) {
  if (!MODES.includes(mode)) return;
  const key = String(userId ?? '');
  const store = storageOf(storage);
  const map = readMap(store);
  // Collection is the default, so only Observation is kept.
  if (mode === COLLECTION) delete map[key];
  else map[key] = mode;
  try {
    if (Object.keys(map).length) store.setItem(FIELD_MODE_KEY, JSON.stringify(map));
    else store.removeItem(FIELD_MODE_KEY);
    unkept.delete(key);
  } catch {
    unkept.set(key, mode);
  }
  try {
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: key }));
  } catch {
    /* no events here: the next read finds it */
  }
}

/** [mode, setMode] for this person, following changes in this tab and others. */
export function useFieldMode(userId, canCollect) {
  const [mode, setMode] = useState(() => readFieldMode(userId, canCollect));
  useEffect(() => {
    const refresh = () => setMode(readFieldMode(userId, canCollect));
    refresh();
    const onStorage = (e) => { if (!e || e.key === null || e.key === FIELD_MODE_KEY) refresh(); };
    window.addEventListener('storage', onStorage);
    window.addEventListener(CHANGE_EVENT, refresh);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(CHANGE_EVENT, refresh);
    };
  }, [userId, canCollect]);
  return [mode, (value) => setFieldMode(userId, value)];
}

/**
 * What sending this batch will make, by the outbox's own rule
 * (fieldOutbox.js, the mint step), so the capture summary and the details
 * form never promise something else:
 *   'labelled'     a typed or scanned label becomes its code: a collection
 *   'minted'       it already holds an Isolate ID: a collection
 *   'mint'         an Isolate ID will be minted: a collection
 *   'observation'  no code: a sighting, no plates
 */
export function batchOutcome(batch, canMint) {
  const details = batch?.details || {};
  if (normaliseTag(details.label)) return 'labelled';
  if (details.observeOnly) return 'observation';
  if (batch?.specimenCode) return 'minted';
  return canMint ? 'mint' : 'observation';
}

/** For tests: forget what this page held for a storage that refused. */
export function forgetUnkeptFieldMode() {
  unkept.clear();
}
